import { useRef, useState } from "react";
import { jahrgangTitel } from "../kern/fachlogik.js";
import { fmtZahl, parseJahr, parseZahl, sortDe, uid } from "../kern/format.js";
import { EIGENE_KRITIK, NEU, NV, SCHEMATA, TYPEN } from "../kern/konstanten.js";
import { landName } from "../i18n/laender.js";
import { RegionSubForm, baueNeueRegion, prufeNeueRegion } from "./stammdaten.jsx";
import { useFotoWahl } from "../ui/hooks.jsx";
import {
  Auswahl, Btn, Eingabe, Feld, FotoBild, ModalFuss, SuchAuswahl,
  TextLoeschen, Textbereich, VorschlagEingabe,
} from "../ui/primitive.jsx";
import { t } from "../i18n/index.js";


/* ═══════════════════════════════════════════════════════════════
   Kritik erfassen und zuordnen

   Eine Verkostung passiert selten am Schreibtisch: Glas in der Hand,
   fremdes Etikett, und bis der Wein sauber angelegt wäre, ist der
   Eindruck verflogen. Dieser Dialog dreht die Reihenfolge um — erst
   der Eindruck, dann die Buchhaltung.

   Drei Wege, ein Formular:

   1. Wein bekannt → Jahrgang wählen, fertig. Die Kritik hängt sofort
      am richtigen Platz, und die Ansicht springt dorthin.
   2. Wein neu → Name, Winzer und Typ genügen; der Rest lässt sich
      später am Wein nachtragen. Mehr hier zu verlangen hieße, dem
      Nutzer im Moment der Verkostung ein Formular vorzuhalten.
   3. Wein unklar → nur ein getippter Name und ein Foto. Die Kritik
      bleibt offen, erscheint im Trinkverlauf und wartet dort.

   Das Foto darf in allen drei Fällen sofort aufgenommen werden —
   das ist der Punkt der ganzen Übung, denn das Etikett ist genau
   das, was später beim Zuordnen hilft und was man in dem Moment vor
   sich hat. Es liegt bis zur Zuordnung an der Kritik und wandert
   dann an den Jahrgang, ohne erneut übertragen zu werden.
   ═══════════════════════════════════════════════════════════════ */


const OFFEN = "__offen__";       /* „später zuordnen" */
const NEUER_JG = "__neuerjg__";  /* Jahrgang gibt es noch nicht */


/* Kleiner Etikettenblock: Bild oder Aufnahmeknöpfe. */
function FotoFeld({ foto, wahl }) {
  return (
    <div className="wl-kritikfoto">
      {wahl.eingaben}
      {/* Fester Rahmen: `.wl-foto` ist auf `height: 100%` ausgelegt
          und braucht einen Behälter mit Maß. Ohne ihn blieb die Höhe
          des Feldes hinter der des Bildes zurück, und die Knöpfe
          darunter schoben sich in den Dialogfuß. */}
      {foto && (
        <span className="wl-kritikfoto-rahmen">
          <FotoBild pfad={foto.thumb || foto.pfad} alt={t("Etikett")} />
        </span>
      )}
      <div className="wl-kritikfoto-aktionen">
        {foto ? (
          <>
            <Btn variante="ghost" onClick={wahl.datei} disabled={wahl.laedt}>{t("Anderes Bild")}</Btn>
            <Btn variante="ghost" onClick={wahl.entfernen} disabled={wahl.laedt}>{t("Entfernen")}</Btn>
          </>
        ) : (
          <>
            <Btn variante="linie" onClick={wahl.kamera} disabled={wahl.laedt}>
              {wahl.laedt ? t("Wird übertragen …") : t("Etikett fotografieren")}
            </Btn>
            <Btn variante="ghost" onClick={wahl.datei} disabled={wahl.laedt}>{t("Bild wählen")}</Btn>
          </>
        )}
      </div>
      {wahl.fehler && <p className="wl-fehler wl-kritikfoto-fehler">{wahl.fehler}</p>}
    </div>
  );
}


/**
 * Ein Dialog für zwei Aufgaben, weil es dieselbe ist: einer Kritik
 * ihren Wein geben.
 *
 * - `objekt == null`  → neue Kritik erfassen (Bewertung inbegriffen)
 * - `objekt` gesetzt  → offene Kritik nachträglich zuordnen; die
 *                       Bewertung steht schon, es fehlt nur der Wein
 *
 * `onSave` bekommt ein beschreibendes Ergebnis und nicht die fertigen
 * Datensätze — was daraus im Kellerbuch entsteht (neuer Winzer, neuer
 * Wein, neuer Jahrgang), entscheidet die Schale.
 */
function SchnellKritikForm({
  objekt = null, weine, winzerListe, regionen, katalog = null, vorschlaege,
  kritikPrefs = null, onSave, onCancel, onDelete = null,
}) {
  const zuordnen = !!objekt;
  /* Die ID steht vor dem Speichern fest, weil das Foto sie als
     Dateipfad braucht — es wird ja bereits hochgeladen, während der
     Wein noch offen ist. */
  const kritikId = useRef(objekt ? objekt.id : uid()).current;
  const [foto, setFoto] = useState(objekt ? objekt.foto || null : null);
  const fotoWahl = useFotoWahl({ id: kritikId, foto, setzeFoto: setFoto });
  const [w, setW] = useState(() => ({
    /* Vorbelegung „Ohne Zuordnung": Wer den Dialog aus dem
       Trinkverlauf öffnet, hat meist ein Glas vor sich und noch keine
       Zuordnung im Kopf. So lässt sich die Bewertung ohne einen
       einzigen Umweg festhalten; wer den Wein kennt, wählt ihn
       ausdrücklich. Beim nachträglichen Zuordnen (`objekt` gesetzt)
       bleibt das Feld leer — dort ist die Wahl ja der Zweck. */
    weinId: objekt ? (objekt.weinId || "") : OFFEN,
    jgId: "",
    jahr: "",
    /* Freitext, solange kein Wein feststeht. */
    weinText: objekt?.weinText ?? "",
    /* Angaben für einen neuen Wein */
    neuName: objekt?.weinText ?? "",
    neuWinzerId: winzerListe[0]?.id ?? NEU,
    neuTyp: "rot",
    neuWinzerName: "",
    neuWinzerRegionId: regionen[0]?.id ?? NEU,
    /* Bewertung */
    kritiker: objekt?.kritiker ?? EIGENE_KRITIK,
    schema: objekt?.schema ?? (kritikPrefs?.schema || "p100"),
    wert: objekt?.wert != null ? String(objekt.wert) : "",
    anlass: objekt?.anlass ?? "",
    pairing: objekt?.pairing ?? "",
    text: objekt?.text ?? "",
    /* Bei Zuordnung: bestehendes Etikett des Jahrgangs ersetzen? */
    fotoUebernehmen: true,
  }));
  /* Angaben für eine unterwegs angelegte Region — wie im Weinformular. */
  const [nr, setNr] = useState({ name: "", land: "", lat: "", lon: "" });
  const [fehler, setFehler] = useState(null);
  const setze = (k) => (e) => setW((v) => ({ ...v, [k]: e.target.value }));
  const schema = SCHEMATA[w.schema];

  /* ── Katalog ───────────────────────────────────────────────────
     Im Cloud-Betrieb steht hinter den eigenen Objekten der geteilte
     Katalog. Er wird in dieselben Auswahllisten gemischt statt in
     eine zweite Suche daneben: Wer eine Verkostung festhält, sucht
     einen Wein — nicht erst die Frage, in welchem Bestand er liegen
     könnte. Was gewählt wird, holt die Schale beim Speichern ins
     eigene Kellerbuch (siehe dienste/katalog.js). */
  const katWeine = katalog ? (katalog.weine || []).filter((x) => !weine.some((y) => y.id === x.id)) : [];
  const katWinzer = katalog ? (katalog.winzer || []).filter((x) => !winzerListe.some((y) => y.id === x.id)) : [];
  const katRegionen = katalog ? (katalog.regionen || []).filter((x) => !regionen.some((y) => y.id === x.id)) : [];

  const alleWeine = [...weine, ...katWeine];
  const alleWinzer = [...winzerListe, ...katWinzer];
  const alleRegionen = [...regionen, ...katRegionen];

  /* Zwei „Müller" sind ohne ihre Region nicht auseinanderzuhalten.
     Diese Helfer stehen VOR den Auswahllisten, die sie benutzen —
     als const-Pfeilfunktionen wären sie sonst noch nicht zugänglich. */
  const regionName = (id) => {
    const r = alleRegionen.find((x) => x.id === id);
    return r ? r.name : "";
  };

  /* Kennzeichnet Einträge, die noch nicht im eigenen Kellerbuch
     stehen — sonst wüsste niemand, dass gleich etwas übernommen wird. */
  const ausKatalog = (neben) => [neben, t("Katalog")].filter(Boolean).join(" · ");

  const winzerNameVon = (id) => {
    const o = alleWinzer.find((x) => x.id === id);
    return o ? o.name : "";
  };

  /* Die beiden Auswege stehen OBEN. Wer sie braucht, weiß bereits,
     dass der Wein nicht in der Liste steht — ihn erst an hunderten
     Weinen vorbeischeuchen zu lassen, wäre der falsche Weg herum.
     Sie verschwinden auch nicht beim Tippen: Ein Name, der zu nichts
     passt, ist genau der Fall, in dem sie gebraucht werden. */
  const weinOptionen = [
    { wert: NEU, label: t("＋ Neuen Wein anlegen"), betont: true, immer: true },
    ...(zuordnen ? [] : [{ wert: OFFEN, label: t("Ohne Zuordnung anlegen"), betont: true, immer: true }]),
    ...[...weine].sort(sortDe).map((x) => ({
      wert: x.id, label: x.name,
      neben: [winzerNameVon(x.winzerId), regionName(x.regionId)].filter(Boolean).join(" · "),
    })),
    ...[...katWeine].sort(sortDe).map((x) => ({
      wert: x.id, label: x.name,
      neben: ausKatalog([winzerNameVon(x.winzerId), regionName(x.regionId)].filter(Boolean).join(" · ")),
    })),
  ];

  const gewaehlterWein = alleWeine.find((x) => x.id === w.weinId) || null;
  const istNeuerWein = w.weinId === NEU;
  const istOffen = w.weinId === OFFEN;
  const istNeuerWinzer = istNeuerWein && w.neuWinzerId === NEU;
  const istNeueRegion = istNeuerWinzer && w.neuWinzerRegionId === NEU;

  const jahrgaenge = gewaehlterWein
    ? [...gewaehlterWein.jahrgaenge].sort((a, b) => (b.jahr || 0) - (a.jahr || 0))
    : [];
  /* Beim Wechsel des Weins den Jahrgang zurücksetzen — ein Jahrgang
     des vorigen Weins wäre schlicht falsch. */
  const setzeWein = (e) => setW((v) => ({ ...v, weinId: e.target.value, jgId: "", jahr: "" }));

  const jgWahl = w.jgId || (jahrgaenge[0] ? jahrgaenge[0].id : NEUER_JG);
  /* Das Jahr wird nur getippt, wenn es den Jahrgang noch nicht gibt. */
  const zeigeJahrFeld = istNeuerWein || (!!gewaehlterWein && jgWahl === NEUER_JG);

  const gewaehlterJg = jahrgaenge.find((j) => j.id === jgWahl) || null;
  const etikettVorhanden = !!(gewaehlterJg && gewaehlterJg.foto);

  const speichern = () => {
    /* ── Bewertung prüfen (beim Zuordnen steht sie schon) ── */
    let wert = objekt ? objekt.wert : null;
    if (!zuordnen) {
      if (!w.kritiker.trim()) return setFehler(t("Bitte den Kritiker bzw. die Quelle angeben."));
      wert = parseZahl(w.wert);
      if (wert == null || wert < schema.min || wert > schema.max) {
        return setFehler(t("Der Wert muss zwischen {min} und {max} liegen ({label}).",
          { min: fmtZahl(schema.min, 2), max: fmtZahl(schema.max, 2), label: schema.label }));
      }
    }

    const bewertung = zuordnen ? null : {
      kritiker: w.kritiker.trim(), schema: w.schema, wert,
      anlass: w.anlass.trim(), pairing: w.pairing.trim(), text: w.text.trim(),
    };

    /* ── Offen lassen ── */
    if (istOffen) {
      /* Die Notiz ist ausdrücklich keine Pflicht. Wer im Stehen eine
         Bewertung festhält, weiß selbst am besten, woran er den Wein
         später wiedererkennt — vielleicht am Foto, vielleicht am
         Datum. Ihm hier ein Feld abzuverlangen, hieße, die Erfassung
         genau in dem Moment auszubremsen, für den sie gedacht ist. */
      return melde(onSave({ id: kritikId, foto, modus: "offen", weinText: w.weinText.trim(), bewertung }));
    }

    /* ── Neuer Wein ── */
    if (istNeuerWein) {
      if (!w.neuName.trim()) return setFehler(t("Bitte einen Namen für den Wein angeben."));
      if (istNeuerWinzer && !w.neuWinzerName.trim()) return setFehler(t("Bitte einen Namen für den Winzer angeben."));
      if (istNeuerWinzer && !w.neuWinzerRegionId) return setFehler(t("Bitte eine Region für den neuen Winzer wählen."));
      let neueRegion = null;
      if (istNeueRegion) {
        const err = prufeNeueRegion(nr);
        if (err) return setFehler(t(err));
        neueRegion = baueNeueRegion(nr);
      }
      const jahr = jahrWert();
      if (jahr === false) return;
      return melde(onSave({
        id: kritikId, foto, modus: "neuerWein", bewertung, jahr,
        wein: { name: w.neuName.trim(), typ: w.neuTyp },
        winzer: istNeuerWinzer
          ? { name: w.neuWinzerName.trim(), regionId: istNeueRegion ? null : w.neuWinzerRegionId }
          : { id: w.neuWinzerId },
        neueRegion,
        fotoUebernehmen: true,
      }));
    }

    /* ── Bestehender Wein ── */
    if (!gewaehlterWein) return setFehler(t("Bitte einen Wein wählen."));
    if (jgWahl === NEUER_JG) {
      const jahr = jahrWert();
      if (jahr === false) return;
      return melde(onSave({
        id: kritikId, foto, modus: "wein", bewertung, weinId: gewaehlterWein.id, neuerJahrgang: jahr,
        fotoUebernehmen: true,
      }));
    }
    return melde(onSave({
      id: kritikId, foto, modus: "wein", bewertung, weinId: gewaehlterWein.id, jgId: jgWahl,
      fotoUebernehmen: etikettVorhanden ? w.fotoUebernehmen : true,
    }));
  };

  /* Jahr aus dem Feld — `false` heißt: Fehler gesetzt, abbrechen.
     Leeres Feld gilt als N.V., das ist bei Schaumwein der Normalfall. */
  const jahrWert = () => {
    const roh = String(w.jahr).trim();
    if (!roh) return NV;
    const j = parseJahr(roh);
    if (j == null) { setFehler(t("Bitte ein gültiges Jahr angeben oder das Feld für N.V. leer lassen.")); return false; }
    return j;
  };

  const melde = (err) => { if (err) setFehler(err); };

  return (
    <>
      <p className="wl-hinweis">
        {zuordnen
          ? t("Diese Kritik wartet noch auf ihren Wein. Wähle den passenden aus oder lege ihn neu an.")
          : t("Erst der Eindruck, dann die Zuordnung: Wenn der Wein noch nicht feststeht, genügen ein Name und ein Foto des Etiketts.")}
      </p>

      <div className="wl-formgitter">
        <Feld label={t("Wein")} breit>
          <SuchAuswahl value={w.weinId} onChange={setzeWein} optionen={weinOptionen}
            platzhalter={t("Wein suchen oder wählen …")} />
        </Feld>

        {istOffen && (
          <Feld label={t("Wein (Notiz)")} breit>
            <Eingabe value={w.weinText} onChange={setze("weinText")}
              placeholder={t("z. B. „Riesling, grünes Etikett, Mosel\"")} />
          </Feld>
        )}

        {istNeuerWein && (
          <>
            <Feld label={t("Name des Weins")}>
              <Eingabe value={w.neuName} onChange={setze("neuName")} />
            </Feld>
            <Feld label={t("Typ")}>
              <Auswahl value={w.neuTyp} onChange={setze("neuTyp")}>
                {Object.entries(TYPEN).map(([k, v]) => <option key={k} value={k}>{t(v.label)}</option>)}
              </Auswahl>
            </Feld>
            <Feld label={t("Winzer")} breit>
              {/* Wie bei der Weinauswahl steht der Ausweg oben und
                  überdauert die Suche: Wer ihn braucht, weiß schon,
                  dass der Winzer nicht in der Liste steht. */}
              <SuchAuswahl value={w.neuWinzerId} onChange={setze("neuWinzerId")}
                optionen={[
                  { wert: NEU, label: t("＋ Neuen Winzer anlegen"), betont: true, immer: true },
                  ...[...winzerListe].sort(sortDe).map((x) => ({
                    wert: x.id, label: x.name, neben: regionName(x.regionId),
                  })),
                  ...[...katWinzer].sort(sortDe).map((x) => ({
                    wert: x.id, label: x.name, neben: ausKatalog(regionName(x.regionId)),
                  })),
                ]} />
            </Feld>
            {istNeuerWinzer && (
              <>
                <Feld label={t("Name des Winzers")}>
                  <Eingabe value={w.neuWinzerName} onChange={setze("neuWinzerName")} />
                </Feld>
                <Feld label={t("Region")}>
                  <SuchAuswahl value={w.neuWinzerRegionId} onChange={setze("neuWinzerRegionId")}
                    optionen={[
                      { wert: NEU, label: t("＋ Neue Region anlegen"), betont: true, immer: true },
                      ...[...regionen].sort(sortDe).map((r) => ({
                        wert: r.id, label: r.name, neben: r.land ? landName(r.land) : "",
                      })),
                      ...[...katRegionen].sort(sortDe).map((r) => ({
                        wert: r.id, label: r.name, neben: ausKatalog(r.land ? landName(r.land) : ""),
                      })),
                    ]} />
                </Feld>
                {/* Dritte Ebene: Wein → Winzer → Region. Genau wie im
                    Weinformular, damit unterwegs nichts fehlt. */}
                {istNeueRegion && <RegionSubForm nr={nr} setNr={setNr} vorschlaege={vorschlaege} />}
              </>
            )}
          </>
        )}

        {gewaehlterWein && (
          <Feld label={t("Jahrgang")} breit>
            <Auswahl value={jgWahl} onChange={(e) => setW((v) => ({ ...v, jgId: e.target.value }))}>
              {jahrgaenge.map((jg) => (
                <option key={jg.id} value={jg.id}>{jahrgangTitel(gewaehlterWein, jg)}</option>
              ))}
              <option value={NEUER_JG}>{t("＋ Neuer Jahrgang")}</option>
            </Auswahl>
          </Feld>
        )}

        {zeigeJahrFeld && (
          <Feld label={t("Jahr (leer = N.V.)")}>
            <Eingabe value={w.jahr} onChange={setze("jahr")} inputMode="numeric" placeholder="2018" />
          </Feld>
        )}

        {!zuordnen && (
          <>
            <Feld label={t("Kritiker / Quelle")}>
              <VorschlagEingabe vorschlaege={vorschlaege ? vorschlaege.kritiker : []}
                value={w.kritiker} onChange={setze("kritiker")} placeholder="z. B. Jancis Robinson" />
            </Feld>
            <Feld label={t("Bewertungsschema")}>
              <Auswahl value={w.schema} onChange={setze("schema")}>
                {Object.entries(SCHEMATA).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
              </Auswahl>
            </Feld>
            <Feld label={`${t("Wert")} (${fmtZahl(schema.min, 2)}–${fmtZahl(schema.max, 2)})`}>
              <Eingabe value={w.wert} onChange={setze("wert")} inputMode="decimal" />
            </Feld>
            <Feld label={t("Anlass")}>
              <VorschlagEingabe vorschlaege={vorschlaege ? vorschlaege.anlass : []}
                value={w.anlass} onChange={setze("anlass")} placeholder="z. B. Blindprobe" />
            </Feld>
            <Feld label={t("Speisenbegleitung (Pairing)")}>
              <VorschlagEingabe vorschlaege={vorschlaege ? vorschlaege.pairing : []}
                value={w.pairing} onChange={setze("pairing")} placeholder="z. B. Reh, Hartkäse" />
            </Feld>
            <Feld label={t("Verkostungsnotiz")} breit>
              <Textbereich value={w.text} onChange={setze("text")} rows={3} />
            </Feld>
          </>
        )}

        <Feld label={t("Etikett")} breit>
          <FotoFeld foto={foto} wahl={fotoWahl} />
        </Feld>
      </div>

      {/* Nur wenn wirklich etwas verdrängt würde. */}
      {foto && etikettVorhanden && (
        <label className="wl-kritikersatz">
          <input type="checkbox" checked={w.fotoUebernehmen}
            onChange={(e) => setW((v) => ({ ...v, fotoUebernehmen: e.target.checked }))} />
          <span>{t("Dieser Jahrgang hat bereits ein Etikett — durch das neue ersetzen")}</span>
        </label>
      )}

      {fehler && <p className="wl-fehler">{fehler}</p>}
      <ModalFuss
        onCancel={onCancel} onSave={speichern}
        saveLabel={zuordnen ? t("Zuordnen") : t("Kritik speichern")}
        links={onDelete ? <TextLoeschen onDelete={onDelete} /> : null}
      />
    </>
  );
}


export { SchnellKritikForm };
